import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Anchor,
  Breadcrumbs,
  LoadingOverlay,
  Tabs,
  Space,
} from "@mantine/core";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import EmployeeTicketsList from "../components/EmployeeTicketsList";
import EmployeeTicketDetails from "../components/EmployeeTicketDetails";
import NoEmployeeTicketSelected from "../components/NoEmployeeTicketSelected";
import LeaveExportsView from "../components/LeaveExportsView";
import QuerySearchInput from "../components/QuerySearchInput";
import QueryFilterButton from "../components/QueryFilterButton";
import InboxFilterDrawer from "../components/InboxFilterDrawer";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import useInboxQueryHooks from "../hooks/InboxQueryListHook";
import { useFilterOptionsApiService } from "../service/api/InboxQueryApiService";
import { InboxTabs, InboxFilterState, SerializedFilters } from "../utils/types";
import { AuthContext } from "../../../providers/AuthProvider";
import {
  serializeInboxFilters,
  deserializeInboxFilters,
  getInboxActiveFilterCount,
  getDefaultInboxFilters,
  cleanInboxFilters,
  isInDefaultFilterState,
} from "../utils/inboxFilterUtils";
import UserAccessControlUtils, {
  USER_ROLE_MAPPING,
  FEATURE_LEAVE_MANAGEMENT,
} from "../../../utils/UserAccessControlUtils";

const InboxQueryPage: React.FC = () => {
  const auth = useContext(AuthContext);
  const isViewer = auth?.user_role === USER_ROLE_MAPPING.VIEWER;
  const hasLeaveFeature = UserAccessControlUtils.hasFeature(
    auth?.user_features ?? [],
    FEATURE_LEAVE_MANAGEMENT
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<InboxTabs>(InboxTabs.OPEN);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filterDrawerOpened, setFilterDrawerOpened] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<InboxFilterState>(
    getDefaultInboxFilters()
  );
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const inboxLeaveExportsEnabled = useEnabledFeatureFlag(
    FeatureFlags.QUERY_INBOX_LEAVE_EXPORTS_ENABLED
  );

  const canAccessExports =
    !isViewer && inboxLeaveExportsEnabled && hasLeaveFeature;

  const inboxFiltersEnabled = useEnabledFeatureFlag(
    FeatureFlags.QUERY_INBOX_FILTERS
  );

  const { data: filterOptions } = useFilterOptionsApiService();

  const {
    uuid,
    page,
    pages,
    tickets,
    ticket,
    setTicket,
    openCount,
    resolvedCount,
    currentCount,
    pageLoaderVisible,
    setTicketId,
    setPage,
  } = useInboxQueryHooks(activeTab, searchQuery, appliedFilters);

  const navigate = useNavigate();
  const location = useLocation();

  const handleBreadcrumbClick = useCallback(() => {
    navigate("/query", { state: { activeTab: ticket?.status ?? activeTab } });
  }, [navigate, ticket?.status, activeTab]);

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      // Reset to first page when searching
      setPage(1);
      // Clear selected ticket when searching
      setTicket(null);
      setTicketId(null);
    },
    [setPage, setTicket, setTicketId]
  );

  const handleApplyFilters = useCallback(
    (filters: InboxFilterState) => {
      setAppliedFilters(filters);
      // Reset to first page when applying filters
      setPage(1);
      // Clear selected ticket when applying filters
      setTicket(null);
      setTicketId(null);
    },
    [setPage, setTicket, setTicketId]
  );

  const handleClearAllFilters = useCallback(() => {
    setAppliedFilters(getDefaultInboxFilters());
    // Reset to first page when clearing filters
    setPage(1);
    // Clear selected ticket when clearing filters
    setTicket(null);
    setTicketId(null);
  }, [setPage, setTicket, setTicketId]);

  // Effect to set the active tab based on the location state
  useEffect(() => {
    if (location?.state?.activeTab) {
      const requestedTab = location.state.activeTab;
      if (requestedTab === InboxTabs.EXPORTS && !canAccessExports) {
        setActiveTab(InboxTabs.OPEN);
      } else {
        setActiveTab(requestedTab);
      }
    }
  }, [location.state, canAccessExports]);

  // Effect to initialize search and filters from URL parameters (runs once on mount)
  useEffect(() => {
    const searchFromUrl = searchParams.get("search") || "";
    setSearchQuery(searchFromUrl);

    // Initialize filters from URL if filter options are available
    if (filterOptions) {
      const filtersFromUrl: SerializedFilters = {
        categories: searchParams.get("categories") || undefined,
        queryTypes: searchParams.get("queryTypes") || undefined,
        leaveTypes: searchParams.get("leaveTypes") || undefined,
        statuses: searchParams.get("statuses") || undefined,
      };

      if (
        filtersFromUrl.categories ||
        filtersFromUrl.queryTypes ||
        filtersFromUrl.leaveTypes ||
        filtersFromUrl.statuses
      ) {
        const deserializedFilters = deserializeInboxFilters(filtersFromUrl);
        const cleanedFilters = cleanInboxFilters(
          deserializedFilters,
          filterOptions
        );
        setAppliedFilters(cleanedFilters);
      }

      // Mark initialization as complete
      setIsInitialized(true);
    }
  }, [filterOptions]); // Only depend on filterOptions, not searchParams

  // Effect to update URL when search or filters change (only after initialization)
  useEffect(() => {
    if (!isInitialized) return; // Don't update URL during initialization

    const params = new URLSearchParams(searchParams);

    // Handle search parameter
    if (searchQuery && searchQuery.length >= 2) {
      params.set("search", searchQuery);
    } else {
      params.delete("search");
    }

    // Handle filter parameters
    const serializedFilters = serializeInboxFilters(appliedFilters);
    if (serializedFilters.categories) {
      params.set("categories", serializedFilters.categories);
    } else {
      params.delete("categories");
    }
    if (serializedFilters.queryTypes) {
      params.set("queryTypes", serializedFilters.queryTypes);
    } else {
      params.delete("queryTypes");
    }
    if (serializedFilters.leaveTypes) {
      params.set("leaveTypes", serializedFilters.leaveTypes);
    } else {
      params.delete("leaveTypes");
    }
    if (serializedFilters.statuses) {
      params.set("statuses", serializedFilters.statuses);
    } else {
      params.delete("statuses");
    }

    setSearchParams(params, { replace: true });
  }, [isInitialized, searchQuery, appliedFilters, setSearchParams]); // Removed searchParams to prevent circular dependency

  const getActiveTab: (tab: InboxTabs) => number = (tab: InboxTabs) => {
    let index;

    switch (tab) {
      case InboxTabs.OPEN:
        index = 0;
        break;
      case InboxTabs.RESOLVED:
        index = 1;
        break;
      case InboxTabs.EXPORTS:
        index = 2;
        break;
      default:
        index = 0;
    }

    return index;
  };

  const renderExportTabBody = (): JSX.Element | null => {
    if (activeTab === InboxTabs.EXPORTS && canAccessExports) {
      return <LeaveExportsView />;
    }
    return null;
  };

  const renderTicketListBody = (): JSX.Element | null => {
    if (!uuid && activeTab !== InboxTabs.EXPORTS)
      return (
        <EmployeeTicketsList
          tickets={tickets}
          ticketId={ticket?.id ?? ticket?.query_id ?? uuid}
          setTicketId={setTicketId}
          count={currentCount ?? 0}
          page={page}
          pages={pages}
          setPage={setPage}
          searchQuery={searchQuery}
        />
      );

    return null;
  };

  const renderTicketDetailBody = (): JSX.Element | null => {
    if (
      [InboxTabs.OPEN, InboxTabs.RESOLVED].includes(activeTab) &&
      (ticket || uuid)
    ) {
      return ticket ? (
        <EmployeeTicketDetails
          ticket={ticket}
          setTicket={setTicket}
          setTicketId={setTicketId}
          inboxQueryTab={activeTab}
        />
      ) : (
        <NoEmployeeTicketSelected />
      );
    }

    return null;
  };

  const renderSearchInput = (): JSX.Element | null => {
    if (!uuid && activeTab !== InboxTabs.EXPORTS) {
      return (
        <>
          <Space h="md" />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <QuerySearchInput
                value={searchQuery}
                onSearch={handleSearch}
                disabled={pageLoaderVisible}
                isLoading={pageLoaderVisible}
              />
            </div>
            {inboxFiltersEnabled && (
              <div style={{ flexShrink: 0 }}>
                <QueryFilterButton
                  disabled={pageLoaderVisible}
                  onClick={() => setFilterDrawerOpened(true)}
                  filterCount={getInboxActiveFilterCount(
                    appliedFilters,
                    filterOptions
                  )}
                  showIndicator={!isInDefaultFilterState(appliedFilters)}
                  onClearFilters={handleClearAllFilters}
                />
              </div>
            )}
          </div>
        </>
      );
    }
    return null;
  };

  // Helper function to get tab from index
  const getTabFromIndex = (index: number): InboxTabs => {
    const tabMap: Record<number, InboxTabs> = {
      0: InboxTabs.OPEN,
      1: InboxTabs.RESOLVED,
      2: InboxTabs.EXPORTS,
    };
    return tabMap[index] || InboxTabs.OPEN;
  };

  // Helper function to handle tab change
  const handleTabChange = (index: number): void => {
    const tab = getTabFromIndex(index);
    setActiveTab(tab);
    setPage(1);
    setTicket(null);
    setTicketId(null);
  };

  // Helper function to get tab label with count
  const getTabLabel = (tab: InboxTabs, baseLabel: string): string => {
    const isCurrentTab = activeTab === tab;

    if (!isCurrentTab || pageLoaderVisible) {
      return baseLabel;
    }

    let count: number | null = null;
    if (searchQuery) {
      count = currentCount;
    } else if (tab === InboxTabs.OPEN) {
      count = openCount;
    } else {
      count = resolvedCount;
    }

    if (count !== null) {
      return `${baseLabel} (${count})`;
    }

    return baseLabel;
  };

  const renderTabHeaders = (): JSX.Element | null => {
    if (!uuid) {
      return (
        <Tabs
          classNames={{
            root: "mt-7",
            tabControl: "text-sm !text-jem-primary-50t h-[40px]",
            tabActive: "!text-jem-primary-base",
            body: "overflow-y-auto",
          }}
          active={getActiveTab(activeTab)}
          onTabChange={handleTabChange}
        >
          <Tabs.Tab label={getTabLabel(InboxTabs.OPEN, "For review")} />
          <Tabs.Tab label={getTabLabel(InboxTabs.RESOLVED, "Reviewed")} />
          <Tabs.Tab
            label="Exports"
            hidden={!canAccessExports}
          />
        </Tabs>
      );
    }

    return null;
  };

  const renderBreadcrumbs = (): JSX.Element | null => {
    if (uuid && ticket) {
      return (
        <Breadcrumbs
          separator=">"
          classNames={{
            root: "mt-7",
            breadcrumb:
              "text-jem-primary-base hover:no-underline first:text-jem-primary-50t text-sm capitalize",
          }}
        >
          <Anchor onClick={handleBreadcrumbClick}>
            {ticket.status.toLocaleLowerCase()} Queries
          </Anchor>
          <Anchor>
            {ticket.query_type} - {ticket.employee.employee_no} -{" "}
            {ticket.employee.first_name} {ticket.employee.last_name}
          </Anchor>
        </Breadcrumbs>
      );
    }
    return null;
  };

  return (
    <PageContainer className="h-screen max-h-screen">
      <LoadingOverlay visible={pageLoaderVisible} />
      <PageHeading title="Inbox" />
      {/* Breadcrumbs */}
      {renderBreadcrumbs()}

      {/* Tab headers */}
      {renderTabHeaders()}

      {/* Search Input */}
      {renderSearchInput()}

      {/* Tab Bodies */}
      <div className="flex h-full pt-6 overflow-hidden">
        {/* Exports body */}
        {renderExportTabBody()}
        {/* Employee tickets list */}
        {renderTicketListBody()}
        {/* Ticket detail body */}
        {renderTicketDetailBody()}
      </div>

      {/* Filter Drawer */}
      {inboxFiltersEnabled && (
        <InboxFilterDrawer
          opened={filterDrawerOpened}
          onClose={() => setFilterDrawerOpened(false)}
          onApplyFilters={handleApplyFilters}
          initialFilters={appliedFilters}
        />
      )}
    </PageContainer>
  );
};

export default InboxQueryPage;
